import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Resolves an image URL to an absolute URL.
 * Handles both absolute URLs (starting with http) and relative paths.
 * @param {string|null|undefined} url - The image URL to resolve
 * @returns {string|null} Absolute URL or null if no URL provided
 */
export function resolveImageUrl(url) {
  if (!url) return null;
  
  // Already absolute URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Relative path - prepend API_URL
  const baseUrl = API_URL.replace(/\/$/, ''); // Remove trailing slash
  
  // Normalize path - ensure it starts with /
  let path = url;
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  
  // Convert old /uploads/menu_items/ paths to new /api/uploads/menu-items/ format
  if (path.includes('/uploads/menu_items/')) {
    path = path.replace('/uploads/menu_items/', '/api/uploads/menu-items/');
  }
  
  return `${baseUrl}${path}`;
}

/**
 * Returns YYYY-MM-DD string for a date in Europe/Rome timezone
 * @param {Date|string|number} dateLike - Date object, ISO string, or timestamp
 * @returns {string} Date string in YYYY-MM-DD format (Rome timezone)
 */
export function getRomeYMD(dateLike) {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

/**
 * Checks if a date is today in Europe/Rome timezone
 * @param {Date|string|number} dateLike - Date object, ISO string, or timestamp
 * @returns {boolean} True if the date is today in Rome timezone
 */
export function isTodayRome(dateLike) {
  return getRomeYMD(dateLike) === getRomeYMD(new Date());
}

/**
 * Calculates milliseconds until the next midnight in Europe/Rome timezone
 * @returns {number} Milliseconds until midnight Rome time
 */
export function getMsUntilMidnightRome() {
  const now = new Date();
  
  // Get current time parts in Rome timezone
  const romeOptions = { timeZone: 'Europe/Rome', hour12: false };
  const romeHour = parseInt(new Intl.DateTimeFormat('en-US', { ...romeOptions, hour: '2-digit' }).format(now));
  const romeMinute = parseInt(new Intl.DateTimeFormat('en-US', { ...romeOptions, minute: '2-digit' }).format(now));
  const romeSecond = parseInt(new Intl.DateTimeFormat('en-US', { ...romeOptions, second: '2-digit' }).format(now));
  
  // Calculate ms until midnight
  const hoursUntilMidnight = 23 - romeHour;
  const minutesUntilMidnight = 59 - romeMinute;
  const secondsUntilMidnight = 60 - romeSecond;
  
  return (hoursUntilMidnight * 60 * 60 * 1000) + 
         (minutesUntilMidnight * 60 * 1000) + 
         (secondsUntilMidnight * 1000);
}
